import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Header from './Header';
import Footer from './Footer';
import BottomNavigationBar from './BottomNavigationBar';
import ProfileSidebar from './ProfileSidebar';
import ProfileEditModal from './ProfileEditModal';
import SkillsPopup from './SkillsPopup';
import UsernameModal from './UsernameModal';
import ExperiencePopup from './ExperiencePopup';
import AllExperiencesPopup from './AllExperiencesPopup';
import SimpleProjectPopup from './MultiStepProject/SimpleProjectPopup';
import AllProjectsPopup from './AllProjectsPopup';
import PersonalExperience from './PersonalExperience';
import PersonalProjects from './PersonalProjects';
import PersonalEducation from './PersonalEducation';
import EducationPopup from './EducationPopup';
import AllEducationPopup from './AllEducationPopup';
import ProfileSetupTimeline from './ProfileSetupTimeline';
import ResumeManagementPopup from './ResumeManagementPopup';
import ImageViewModal from './ImageViewModal';
import JobPostingCTA from './Jobs/JobPostingCTA';
import JobStats from './Jobs/JobStats';
import ApiService from '../services/api';
import { getApiUrl } from '../config/api';
import './UserDashboard.css';

const UserDashboard = ({ onClose, onHomeClick, onCategoriesClick, onHowItWorksClick, onProfileClick, onAccountSettingsClick, onForumClick }) => {
  const navigate = useNavigate();
  const { user, logout, isAuthenticated, updateUser, getCachedExperiences, getCachedProjects, getCachedEducation } = useAuth();
  // Removed activeTab state - no longer using tabs
  
  // Mode checks - available throughout component
  const currentMode = user?.active_mode || user?.default_mode || 'personal';
  const isCompanyMode = currentMode === 'company';
  const isRecruiterMode = currentMode === 'recruiter';
  const isRecruiterOrCompanyMode = isCompanyMode || isRecruiterMode;
  const isPersonalMode = currentMode === 'personal';
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    dateOfBirth: '',
    profilePhoto: null
  });
  const [previewPhoto, setPreviewPhoto] = useState(null);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [completionStatus, setCompletionStatus] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showSkillsPopup, setShowSkillsPopup] = useState(false);
  const [showUsernameModal, setShowUsernameModal] = useState(false);
  const [userSkills, setUserSkills] = useState([]);
  const [skillsLoading, setSkillsLoading] = useState(false);
  const [userExperiences, setUserExperiences] = useState([]);
  const [experiencesLoading, setExperiencesLoading] = useState(false);
  const [userProjects, setUserProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [userEducation, setUserEducation] = useState([]);
  const [educationLoading, setEducationLoading] = useState(false);
  
  // Resume upload state
  const [userResume, setUserResume] = useState(null);
  const [resumeLoading, setResumeLoading] = useState(false);
  const [isResumeUploading, setIsResumeUploading] = useState(false);
  const [resumeUploadProgress, setResumeUploadProgress] = useState(0);
  const [showResumePopup, setShowResumePopup] = useState(false);
  
  // Banner upload state
  const [bannerImage, setBannerImage] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);
  const [isBannerUploading, setIsBannerUploading] = useState(false);
  const [bannerUploadProgress, setBannerUploadProgress] = useState(0);
  
  // Modal state management
  const [showExperiencePopup, setShowExperiencePopup] = useState(false);
  const [editingExperience, setEditingExperience] = useState(null);
  const [showAllExperiencesPopup, setShowAllExperiencesPopup] = useState(false);
  const [showProjectPopup, setShowProjectPopup] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [showAllProjectsPopup, setShowAllProjectsPopup] = useState(false);
  const [showEducationPopup, setShowEducationPopup] = useState(false);
  const [editingEducation, setEditingEducation] = useState(null);
  const [showAllEducationPopup, setShowAllEducationPopup] = useState(false);
  const [isProfileSidebarOpen, setIsProfileSidebarOpen] = useState(false);
  
  // Image view modal state
  const [showImageViewModal, setShowImageViewModal] = useState(false);
  const [imageViewType, setImageViewType] = useState('profile'); // 'profile' or 'banner'
  
  const fileInputRef = useRef(null);
  const bannerInputRef = useRef(null);
  const resumeInputRef = useRef(null);
  const professionalTitleRef = useRef(null);
  const editButtonRef = useRef(null);
  const nameTitleContainerRef = useRef(null);

  // Calculate profile completion percentage
  const profileCompletion = completionStatus ? completionStatus.percentage : 0;



  // Format date range for better readability
  const formatDateRange = (startDate, endDate, isCurrent) => {
    const formatDate = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short' 
      });
    };

    const start = formatDate(startDate);
    const end = isCurrent ? 'Present' : formatDate(endDate);
    
    return `${start} - ${end}`;
  };


  // Removed tabs - now using left sidebar for skills and basic info

  // No longer needed - edit button is now inline inside the professional title span
  // The button will naturally appear right after the text in the normal document flow

  useEffect(() => {
    if (user) {
      // Split name into firstName and lastName
      const nameParts = (user.name || '').trim().split(/\s+/);
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      
      setFormData({
        firstName: firstName,
        lastName: lastName,
        phone: user.phone || '',
        dateOfBirth: user.date_of_birth || '',
        profilePhoto: null
      });
      setPreviewPhoto(user.profile_photo_url || null);
      setBannerPreview(user.banner_image_url || null);
      fetchCompletionStatus();
      fetchUserSkills();
      fetchUserResume();
      
      // Load cached data first for instant display
      const cachedExperiences = getCachedExperiences();
      const cachedProjects = getCachedProjects();
      
      if (cachedExperiences.length > 0) {
        setUserExperiences(cachedExperiences);
      }
      if (cachedProjects.length > 0) {
        setUserProjects(cachedProjects);
      }
      
      // Data fetching is now handled by the separate components
    }
  }, [user, getCachedExperiences, getCachedProjects]);

  const fetchCompletionStatus = async () => {
    try {
      const response = await ApiService.get('/profile/completion-status');
      if (response.success) {
        setCompletionStatus(response.data);
      }
    } catch (error) {
      // Handle error silently - completion status fetch is not critical
    }
  };

  const fetchUserSkills = async () => {
    setSkillsLoading(true);
    try {
      const response = await ApiService.get('/user-skills');
      if (response.success) {
        setUserSkills(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching user skills:', error);
    } finally {
      setSkillsLoading(false);
    }
  };

  const fetchUserResume = async () => {
    setResumeLoading(true);
    try {
      const response = await ApiService.get('/user/resume/default');
      if (response.success) {
        setUserResume(response.data || null);
      }
    } catch (error) {
      console.error('Error fetching user resume:', error);
    } finally {
      setResumeLoading(false);
    }
  };

  const handleSkillsSave = (updatedSkills) => {
    setUserSkills(updatedSkills);
    setShowSkillsPopup(false);
    // Show success message
    setSuccessMessage('Skills updated successfully!');
    setShowSuccessMessage(true);
    setTimeout(() => setShowSuccessMessage(false), 3000);
  };

  const handleRemoveSkill = async (skillId) => {
    try {
      const response = await ApiService.delete(`/user-skills/${skillId}`);
      if (response.success) {
        setUserSkills(prev => prev.filter(skill => skill.id !== skillId));
        setSuccessMessage('Skill removed successfully!');
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 3000);
      }
    } catch (error) {
      console.error('Error removing skill:', error);
    }
  };

  // Experience modal handlers
  const handleExperienceSave = (updatedExperience) => {
    if (editingExperience) {
      // Update existing experience
      setUserExperiences(prev => 
        prev.map(exp => exp.id === updatedExperience.id ? updatedExperience : exp)
      );
    } else {
      // Add new experience
      setUserExperiences(prev => [updatedExperience, ...prev]);
    }
    setShowExperiencePopup(false);
    setEditingExperience(null);
    setSuccessMessage(editingExperience ? 'Experience updated successfully!' : 'Experience added successfully!');
    setShowSuccessMessage(true);
    setTimeout(() => setShowSuccessMessage(false), 3000);
  };

  // Education modal handlers
  const handleEducationSave = (updatedEducation) => {
    if (editingEducation) {
      // Update existing education
      setUserEducation(prev => 
        prev.map(edu => edu.id === updatedEducation.id ? updatedEducation : edu)
      );
    } else {
      // Add new education
      setUserEducation(prev => [updatedEducation, ...prev]);
    }
    setShowEducationPopup(false);
    setEditingEducation(null);
    setSuccessMessage(editingEducation ? 'Education updated successfully!' : 'Education added successfully!');
    setShowSuccessMessage(true);
    setTimeout(() => setShowSuccessMessage(false), 3000);
  };

  const handleEditExperience = (experience) => {
    setEditingExperience(experience);
    setShowExperiencePopup(true);
  };

  const handleRemoveExperience = async (experienceId) => {
    try {
      const response = await ApiService.delete(`/experiences/${experienceId}`);
      if (response.success) {
        setUserExperiences(prev => prev.filter(exp => exp.id !== experienceId));
        setSuccessMessage('Experience removed successfully!');
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 3000);
      }
    } catch (error) {
      console.error('Error removing experience:', error);
    }
  };

  const handleEditEducation = (education) => {
    setEditingEducation(education);
    setShowEducationPopup(true);
  };

  const handleRemoveEducation = async (educationId) => {
    try {
      const response = await ApiService.delete(`/education/${educationId}`);
      if (response.success) {
        setUserEducation(prev => prev.filter(edu => edu.id !== educationId));
        setSuccessMessage('Education removed successfully!');
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 3000);
      }
    } catch (error) {
      console.error('Error removing education:', error);
    }
  };

  // Project modal handlers
  const handleProjectSave = async (updatedProject) => {
    try {
      // Fetch fresh project data from server to ensure we have the latest information
      const response = await ApiService.personalProjectsAPI.getAll();
      if (response.success && response.data) {
        setUserProjects(response.data);
        console.log('Projects refreshed from server:', response.data);
      } else {
        // Fallback to local update if server fetch fails
        if (editingProject) {
          setUserProjects(prev => 
            prev.map(proj => proj.id === updatedProject.id ? updatedProject : proj)
          );
        } else {
          setUserProjects(prev => [updatedProject, ...prev]);
        }
      }
    } catch (error) {
      console.error('Error fetching updated projects:', error);
      // Fallback to local update if server fetch fails
      if (editingProject) {
        setUserProjects(prev => 
          prev.map(proj => proj.id === updatedProject.id ? updatedProject : proj)
        );
      } else {
        setUserProjects(prev => [updatedProject, ...prev]);
      }
    }
    
    setShowProjectPopup(false);
    setEditingProject(null);
    setSuccessMessage(editingProject ? 'Project updated successfully!' : 'Project added successfully!');
    setShowSuccessMessage(true);
    setTimeout(() => setShowSuccessMessage(false), 3000);
  };

  const handleEditProject = (project) => {
    setEditingProject(project);
    setShowProjectPopup(true);
  };

  const handleRemoveProject = async (projectId) => {
    try {
      const response = await ApiService.delete(`/personal-projects/${projectId}`);
      if (response.success) {
        setUserProjects(prev => prev.filter(proj => proj.id !== projectId));
        setSuccessMessage('Project removed successfully!');
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 3000);
      }
    } catch (error) {
      console.error('Error removing project:', error);
    }
  };




  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
    } catch (error) {
      // Handle logout error silently
    } finally {
      setIsLoggingOut(false);
    }
  };


  const handlePhotoUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleAvatarPhotoUpload = (e) => {
    if (e) {
      e.stopPropagation();
    }
    // Always set type to profile first
    setImageViewType('profile');
    // If profile picture exists, open image view modal
    if (previewPhoto || user.profile_photo_url) {
      setShowImageViewModal(true);
    } else {
      // If no profile picture, directly open file picker
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    }
  };

  const handleBannerUpload = (e) => {
    if (e) {
      e.stopPropagation();
    }
    // Always set type to banner first
    setImageViewType('banner');
    // If banner exists, open image view modal
    if (bannerPreview || user.banner_image_url) {
      setShowImageViewModal(true);
    } else {
      // If no banner, directly open file picker
      if (bannerInputRef.current) {
        bannerInputRef.current.click();
      }
    }
  };

  // Handle image change from modal
  const handleImageChangeFromModal = (file) => {
    if (imageViewType === 'profile') {
      // Handle profile photo upload
      handlePhotoUpload(file);
      // Modal will close automatically after successful upload (handled in handlePhotoUpload success callback)
    } else {
      // Handle banner upload
      uploadBannerImage(file);
      // Modal will close automatically after successful upload (handled in uploadBannerImage success callback)
    }
  };

  // Handle profile photo removal
  const handleRemoveProfilePhoto = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(getApiUrl('/profile/photo'), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setPreviewPhoto(null);
          // Update user state to remove profile photo
          updateUser({
            ...user,
            profile_photo: null,
            profile_photo_url: null
          });
          setSuccessMessage('✅ Profile photo removed successfully!');
          setShowSuccessMessage(true);
          setTimeout(() => setShowSuccessMessage(false), 5000);
          setShowImageViewModal(false);
        } else {
          alert(result.message || 'Failed to remove profile photo');
        }
      } else {
        alert('Failed to remove profile photo');
      }
    } catch (error) {
      console.error('Profile photo removal error:', error);
      alert('Failed to remove profile photo');
    }
  };

  // Handle image remove from modal
  const handleImageRemoveFromModal = () => {
    if (imageViewType === 'profile') {
      handleRemoveProfilePhoto();
    } else {
      handleRemoveBanner();
      setShowImageViewModal(false);
    }
  };

  // Resume upload handlers
  const handleResumeUpload = () => {
    setShowResumePopup(true);
  };

  const handleResumeSelect = (resume) => {
    setUserResume(resume);
    setSuccessMessage('Resume selected successfully!');
    setShowSuccessMessage(true);
    setTimeout(() => setShowSuccessMessage(false), 3000);
  };

  const handleResumeChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      alert('Please upload a PDF or Word document');
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB');
      return;
    }

    setIsResumeUploading(true);
    setResumeUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('resume', file);

      const response = await ApiService.uploadFile('/user/resumes', formData, (progressEvent) => {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setResumeUploadProgress(progress);
      });

      if (response.success) {
        setUserResume(response.data);
        setSuccessMessage('Resume uploaded successfully!');
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 3000);
      }
    } catch (error) {
      console.error('Error uploading resume:', error);
      alert('Failed to upload resume. Please try again.');
    } finally {
      setIsResumeUploading(false);
      setResumeUploadProgress(0);
    }
  };

  const handleRemoveResume = async () => {
    try {
      if (!userResume || !userResume.id) {
        console.error('No resume to delete');
        return;
      }
      
      const response = await ApiService.delete(`/user/resumes/${userResume.id}`);
      if (response.success) {
        setUserResume(null);
        setSuccessMessage('Resume removed successfully!');
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 3000);
      }
    } catch (error) {
      console.error('Error removing resume:', error);
    }
  };


  const handleBannerChange = (e) => {
    console.log('Banner file selected:', e.target.files);
    const file = e.target.files[0];
    if (file) {
      console.log('File details:', {
        name: file.name,
        size: file.size,
        type: file.type
      });
      
      if (file.type.startsWith('image/')) {
        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
          setErrors(prev => ({ ...prev, banner: 'Image size must be less than 5MB' }));
          return;
        }
        
        // Always open modal with cropper for image selection/change
        setImageViewType('banner');
        setBannerImage(file); // Store file to pass to modal
        setShowImageViewModal(true);
        // The modal will automatically process the file and show cropper via initialFile prop
      } else {
        console.error('Invalid file type:', file.type);
        setErrors(prev => ({ ...prev, banner: 'Please select a valid image file' }));
      }
    } else {
      console.log('No file selected');
    }
  };

  const uploadBannerImage = async (file) => {
    console.log('Starting banner upload:', file);
    setIsBannerUploading(true);
    setBannerUploadProgress(0);
    
    try {
      const formData = new FormData();
      formData.append('banner_image', file);
      console.log('FormData created, file size:', file.size, 'file type:', file.type);
      
      const xhr = new XMLHttpRequest();
      
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentComplete = (e.loaded / e.total) * 100;
          setBannerUploadProgress(percentComplete);
        }
      });
      
      xhr.onload = () => {
        console.log('Banner upload response status:', xhr.status);
        console.log('Banner upload response:', xhr.responseText);
        
        if (xhr.status === 200) {
          const response = JSON.parse(xhr.responseText);
          console.log('Parsed response:', response);
          
          if (response.success) {
            setBannerPreview(response.data.banner_image_url);
            // Update user state with new banner
            updateUser({
              ...user,
              banner_image: response.data.banner_image_url,
              banner_image_url: response.data.banner_image_url
            });
            setSuccessMessage('✅ Banner image uploaded successfully!');
            setShowSuccessMessage(true);
            setTimeout(() => setShowSuccessMessage(false), 5000);
            setErrors(prev => ({ ...prev, banner: '' }));
            
            // Close image view modal if open
            if (showImageViewModal && imageViewType === 'banner') {
              setTimeout(() => {
                setShowImageViewModal(false);
              }, 1500);
            }
          } else {
            console.error('Upload failed:', response.message);
            setErrors(prev => ({ ...prev, banner: response.message || 'Failed to upload banner image' }));
          }
        } else {
          console.error('Upload failed with status:', xhr.status);
          setErrors(prev => ({ ...prev, banner: 'Failed to upload banner image' }));
        }
        setIsBannerUploading(false);
        setBannerUploadProgress(0);
      };
      
      xhr.onerror = () => {
        console.error('Network error during banner upload');
        setErrors(prev => ({ ...prev, banner: 'Network error while uploading banner image' }));
        setIsBannerUploading(false);
        setBannerUploadProgress(0);
      };
      
      const token = localStorage.getItem('auth_token');
      const apiUrl = getApiUrl('/profile/banner');
      
      console.log('API URL:', apiUrl);
      console.log('Auth token exists:', !!token);
      console.log('Auth token length:', token ? token.length : 0);
      
      xhr.open('POST', apiUrl, true);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.send(formData);
      
    } catch (error) {
      console.error('Banner upload error:', error);
      setErrors(prev => ({ ...prev, banner: 'Failed to upload banner image' }));
      setIsBannerUploading(false);
      setBannerUploadProgress(0);
    }
  };

  const handleRemoveBanner = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(getApiUrl('/profile/banner'), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setBannerPreview(null);
          // Update user state to remove banner
          updateUser({
            ...user,
            banner_image: null,
            banner_image_url: null
          });
          setSuccessMessage('✅ Banner image removed successfully!');
          setShowSuccessMessage(true);
          setTimeout(() => setShowSuccessMessage(false), 5000);
          setErrors(prev => ({ ...prev, banner: '' }));
        } else {
          setErrors(prev => ({ ...prev, banner: result.message || 'Failed to remove banner image' }));
        }
      } else {
        setErrors(prev => ({ ...prev, banner: 'Failed to remove banner image' }));
      }
    } catch (error) {
      console.error('Banner removal error:', error);
      setErrors(prev => ({ ...prev, banner: 'Failed to remove banner image' }));
    }
  };

  // Handle public profile view
  const handlePublicView = () => {
    if (user && user.username) {
      const publicUrl = `${window.location.origin}/talent/${user.username}`;
      console.log('Opening public profile URL:', publicUrl);
      console.log('User data:', { id: user.id, username: user.username, name: user.name });
      window.open(publicUrl, '_blank');
    } else {
      // Open username modal to let user set their username first
      setShowUsernameModal(true);
    }
  };

  // Get achievement badge based on user's profile and achievements
  const getAchievementBadge = () => {
    const hasProjects = userProjects && userProjects.length > 0;
    const hasExperience = userExperiences && userExperiences.length > 0;
    const hasSkills = userSkills && userSkills.length > 0;
    const hasEducation = userEducation && userEducation.length > 0;

    // Legend: Has experience (most prestigious)
    if (hasExperience) {
      return (
        <div className="achievement-badge legend-badge">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"></path>
            <path d="M8 12l2 2 4-4"></path>
          </svg>
          <span>Legend</span>
        </div>
      );
    }

    // Shining Star: Has projects but no experience
    if (hasProjects) {
      return (
        <div className="achievement-badge shining-star-badge">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"></polygon>
          </svg>
          <span>Shining Star</span>
        </div>
      );
    }

    // Rising Star: No projects, but has skills or education
    if (hasSkills || hasEducation) {
      return (
        <div className="achievement-badge rising-star-badge">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"></path>
          </svg>
          <span>Rising Star</span>
        </div>
      );
    }

    // Newcomer: Just starting out
    return (
      <div className="achievement-badge newcomer-badge">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <path d="M12 6v6l4 2"></path>
        </svg>
        <span>Newcomer</span>
      </div>
    );
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    
    const files = e.dataTransfer.files;
    if (files && files[0]) {
      const file = files[0];
      if (file.type.startsWith('image/')) {
        // Create a fake event object to reuse the handleChange function
        const fakeEvent = {
          target: {
            name: 'profilePhoto',
            files: [file]
          }
        };
        handleChange(fakeEvent);
      } else {
        setErrors(prev => ({ ...prev, profile_photo: 'Please drop a valid image file' }));
      }
    }
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    
    if (name === 'profilePhoto' && files[0]) {
      const file = files[0];
      
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setErrors(prev => ({ ...prev, profile_photo: 'Please select a valid image file' }));
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, profile_photo: 'Image size must be less than 5MB' }));
        return;
      }
      
      // Always open modal with cropper for image selection/change
      setImageViewType('profile');
      setFormData(prev => ({ ...prev, [name]: file }));
      setShowImageViewModal(true);
      // The modal will automatically process the file and show cropper via initialFile prop
      
      // Clear any previous errors
      if (errors.profile_photo) {
        setErrors(prev => ({ ...prev, profile_photo: '' }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setIsUploading(!!formData.profilePhoto);
    setUploadProgress(0);
    setErrors({});
    try {
      
      const formDataToSend = new FormData();
      // Combine firstName and lastName into name
      const fullName = `${formData.firstName.trim()} ${formData.lastName.trim()}`.trim();
      formDataToSend.append('name', fullName);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('date_of_birth', formData.dateOfBirth);
      
      if (formData.profilePhoto) {
        formDataToSend.append('profile_photo', formData.profilePhoto);

        let fileFoundInFormData = false;
        for (let [key, value] of formDataToSend.entries()) {
          if (value instanceof File) {
            fileFoundInFormData = true;
          }
        }
        
        if (!fileFoundInFormData) {
          setErrors({ general: 'File upload error: File not properly added to form data. Please try again.' });
          setIsLoading(false);
          return;
        } else {
          // File successfully added to FormData
        }
      } else {
        // No profile photo file to upload
      }

      const token = ApiService.getToken();
      
      // Check if user has a valid token
      if (!token) {
        setErrors({ general: 'You are not logged in. Please log in again.' });
        setIsLoading(false);
        return;
      }
      
      const xhr = new XMLHttpRequest();
      
      // Track upload progress
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentComplete = (e.loaded / e.total) * 100;
          setUploadProgress(Math.round(percentComplete));
        }
      });

      // Handle response
      xhr.addEventListener('load', () => {
        if (xhr.status === 200) {
          try {
            const data = JSON.parse(xhr.responseText);
            if (data.success) {
              // here need to update the user context and localStorage
              updateUser(data.data.user);
              setIsEditing(false);
              setIsUploading(false);
              setUploadProgress(0);
              setErrors({});
              
              // Update the preview photo with the new uploaded photo
              if (formData.profilePhoto) {
                setPreviewPhoto(URL.createObjectURL(formData.profilePhoto));
              }
              
              // Show professional success message
              const successMsg = formData.profilePhoto 
                ? '🎉 Profile updated successfully! Your photo has been uploaded and saved.'
                : '✅ Profile updated successfully! Your information has been saved.';
              showSuccess(successMsg);
              
              // Clear the form data to prevent re-submission
              setFormData(prev => ({ ...prev, profilePhoto: null }));
            } else {
              // Handle validation errors
              if (data.errors) {
                const formattedErrors = {};
                Object.keys(data.errors).forEach(key => {
                  if (Array.isArray(data.errors[key])) {
                    formattedErrors[key] = data.errors[key][0];
                  } else {
                    formattedErrors[key] = data.errors[key];
                  }
                });
                setErrors(formattedErrors);
              } else {
                setErrors({ general: data.message || 'Profile update failed. Please check your information and try again.' });
              }
              setIsUploading(false);
              setUploadProgress(0);
            }
          } catch (parseError) {
            // Error parsing response
            setErrors({ general: 'Invalid response from server. Please try again.' });
            setIsUploading(false);
            setUploadProgress(0);
          }
        } else if (xhr.status === 401) {
          setErrors({ general: 'Your session has expired. Please log in again.' });
          setIsUploading(false);
          setUploadProgress(0);
        } else if (xhr.status === 422) {
          try {
            const data = JSON.parse(xhr.responseText);
            if (data.errors) {
              const formattedErrors = {};
              Object.keys(data.errors).forEach(key => {
                if (Array.isArray(data.errors[key])) {
                  formattedErrors[key] = data.errors[key][0];
                } else {
                  formattedErrors[key] = data.errors[key];
                }
              });
              setErrors(formattedErrors);
            } else {
              setErrors({ general: data.message || 'Please check your information and try again.' });
            }
          } catch (parseError) {
            setErrors({ general: 'Please check your information and try again.' });
          }
          setIsUploading(false);
          setUploadProgress(0);
        } else if (xhr.status === 413) {
          setErrors({ general: 'File too large. Please choose an image smaller than 5MB.' });
          setIsUploading(false);
          setUploadProgress(0);
        } else if (xhr.status === 500) {
          setErrors({ general: 'Server error. Please try again in a few moments.' });
          setIsUploading(false);
          setUploadProgress(0);
        } else {
          try {
            const data = JSON.parse(xhr.responseText);
            setErrors({ general: data.message || `Update failed (Error ${xhr.status}). Please try again.` });
          } catch (parseError) {
            setErrors({ general: `Update failed (Error ${xhr.status}). Please try again.` });
          }
          setIsUploading(false);
          setUploadProgress(0);
        }
      });

      xhr.addEventListener('error', () => {
        // Network error during profile update
        setErrors({ general: 'Network error. Please check your internet connection and try again.' });
        setIsUploading(false);
        setUploadProgress(0);
      });

      xhr.addEventListener('timeout', () => {
        // Request timeout during profile update
        setErrors({ general: 'Request timed out. Please try again.' });
        setIsUploading(false);
        setUploadProgress(0);
      });

      xhr.open('PUT', `${ApiService.baseURL}/profile`);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.setRequestHeader('Accept', 'application/json');
      // Note: Do NOT set Content-Type header when sending FormData with files
      // The browser will set it automatically with the proper boundary
      xhr.timeout = 30000; // 30 second timeout
      xhr.send(formDataToSend);

    } catch (error) {
      // Handle profile update error
      
      if (error.name === 'NetworkError' || error.message.includes('Network')) {
        setErrors({ general: 'Network error. Please check your internet connection and try again.' });
      } else if (error.name === 'TimeoutError') {
        setErrors({ general: 'Request timed out. Please try again.' });
      } else if (error.message.includes('Failed to fetch')) {
        setErrors({ general: 'Cannot connect to server. Please make sure the backend is running on port 3000.' });
      } else {
        setErrors({ general: `An unexpected error occurred: ${error.message}. Please try again.` });
      }
      setIsUploading(false);
      setUploadProgress(0);
    } finally {
      setIsLoading(false);
    }
  };


  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return null;
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setShowSuccessMessage(true);
    setErrors({}); // Clear any existing errors
    
    // Auto-hide success message after 5 seconds
    setTimeout(() => {
      setShowSuccessMessage(false);
      setSuccessMessage('');
    }, 5000);
  };

  const hideSuccess = () => {
    setShowSuccessMessage(false);
    setSuccessMessage('');
  };

  const handleModalSuccess = (message) => {
    showSuccess(message);
  };

  const handlePhotoUpload = async (file) => {
    if (!file) return;
    
    setIsUploading(true);
    setUploadProgress(0);
    setErrors({});

    try {

      
      const formDataToSend = new FormData();
      formDataToSend.append('profile_photo', file);
      
      let fileFoundInFormData = false;
      for (let [key, value] of formDataToSend.entries()) {
        if (value instanceof File) {
          fileFoundInFormData = true;
        }
      }
      
      if (!fileFoundInFormData) {
        // Critical: File not found in FormData entries
        setErrors({ general: 'File upload error: File not properly added to form data. Please try again.' });
        setIsUploading(false);
        return;
      } 

      const token = ApiService.getToken();
      
      if (!token) {
        setErrors({ general: 'You are not logged in. Please log in again.' });
        setIsUploading(false);
        return;
      }
      
      const xhr = new XMLHttpRequest();
      
      // Track upload progress
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentComplete = (e.loaded / e.total) * 100;
          setUploadProgress(Math.round(percentComplete));
        }
      });

      // Handle response
      xhr.addEventListener('load', () => {
        
        if (xhr.status === 200) {
          try {
            const data = JSON.parse(xhr.responseText);
            if (data.success) {
              setIsUploading(false);
              setUploadProgress(0);
              setErrors({});
              
              // Update the preview photo with the new uploaded photo
              setPreviewPhoto(URL.createObjectURL(file));
              
              // Update user data with complete user model from server
              if (data.user) {
                // Update the auth context with complete user data
                updateUser(data.user);
                
                // Update localStorage with complete user data
                localStorage.setItem('auth_user', JSON.stringify(data.user));
                
                console.log('User data updated successfully:', data.user);
              } else if (data.data && data.data.profile_photo_url) {
                // Fallback: Update just the profile photo URL if full user object not returned
                const currentUser = JSON.parse(localStorage.getItem('auth_user') || '{}');
                const updatedUser = { ...currentUser, profile_photo_url: data.data.profile_photo_url };
                localStorage.setItem('auth_user', JSON.stringify(updatedUser));
                
                // Update auth context with new photo URL
                updateUser({ profile_photo_url: data.data.profile_photo_url });
                
                console.log('Profile photo URL updated:', data.data.profile_photo_url);
              }
              
              // Show success message
              showSuccess('🎉 Profile photo uploaded successfully! Your photo has been saved.');
              
              // Close image view modal if open
              if (showImageViewModal && imageViewType === 'profile') {
                setTimeout(() => {
                  setShowImageViewModal(false);
                }, 1500);
              }
              
            } else {
              setErrors({ general: data.message || 'Photo upload failed. Please try again.' });
              setIsUploading(false);
              setUploadProgress(0);
            }
          } catch (parseError) {
            // Error parsing response
            setErrors({ general: 'Invalid response from server. Please try again.' });
            setIsUploading(false);
            setUploadProgress(0);
          }
        } else if (xhr.status === 422) {
          try {
            const data = JSON.parse(xhr.responseText);
            if (data.errors) {
              const formattedErrors = {};
              Object.keys(data.errors).forEach(key => {
                if (Array.isArray(data.errors[key])) {
                  formattedErrors[key] = data.errors[key][0];
                } else {
                  formattedErrors[key] = data.errors[key];
                }
              });
              setErrors(formattedErrors);
            } else {
              setErrors({ general: data.message || 'Please check your file and try again.' });
            }
          } catch (parseError) {
            setErrors({ general: 'Please check your file and try again.' });
          }
          setIsUploading(false);
          setUploadProgress(0);
        } else {
          try {
            const data = JSON.parse(xhr.responseText);
            setErrors({ general: data.message || `Upload failed (Error ${xhr.status}). Please try again.` });
          } catch (parseError) {
            setErrors({ general: `Upload failed (Error ${xhr.status}). Please try again.` });
          }
          setIsUploading(false);
          setUploadProgress(0);
        }
      });

      xhr.addEventListener('error', () => {
        // Network error during photo upload
        setErrors({ general: 'Network error. Please check your internet connection and try again.' });
        setIsUploading(false);
        setUploadProgress(0);
      });

      xhr.addEventListener('timeout', () => {
        // Request timeout during photo upload
        setErrors({ general: 'Request timed out. Please try again.' });
        setIsUploading(false);
        setUploadProgress(0);
      });

      xhr.open('POST', `${ApiService.baseURL}/profile/upload-photo`);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.setRequestHeader('Accept', 'application/json');
      xhr.timeout = 30000; // 30 second timeout
      xhr.send(formDataToSend);

    } catch (error) {
      // Handle photo upload error
      
      if (error.name === 'NetworkError' || error.message.includes('Network')) {
        setErrors({ general: 'Network error. Please check your internet connection and try again.' });
      } else if (error.name === 'TimeoutError') {
        setErrors({ general: 'Request timed out. Please try again.' });
      } else if (error.message.includes('Failed to fetch')) {
        setErrors({ general: 'Cannot connect to server. Please make sure the backend is running on port 3000.' });
      } else {
        setErrors({ general: `An unexpected error occurred: ${error.message}. Please try again.` });
      }
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const renderPhotoTab = () => {
    if (!user) return <div>Loading...</div>;

    return (
      <div className="photo-tab">
        {/* Photo Header */}
        <div className="photo-header-section">
          <div className="current-photo">
            {previewPhoto || user.profile_photo_url ? (
              <img src={previewPhoto || user.profile_photo_url} alt="Current Profile" />
            ) : (
              <div className="avatar-placeholder">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
              </div>
            )}
          </div>
          <div className="photo-info">
            <h2>Profile Photo</h2>
            <p>Upload a clear photo of yourself to help others recognize you.</p>
            <div className="photo-notes">
              <div className="note-item">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M12 6v6l4 2"></path>
                </svg>
                <span>Supported: JPG, PNG, GIF • Max: 5MB</span>
              </div>
              <div className="note-item">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="5"></circle>
                  <line x1="12" y1="1" x2="12" y2="3"></line>
                  <line x1="12" y1="21" x2="12" y2="23"></line>
                  <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
                  <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
                </svg>
                <span>Clear, well-lit photo recommended</span>
              </div>
            </div>
          </div>
        </div>

        {/* Photo Upload Section */}
        <div className="photo-upload-container">
          <div className="upload-header">
            <div className="upload-title-section">
              <h3>Upload New Photo</h3>
              <p>Choose a high-quality image to update your profile picture</p>
            </div>
            <div className="upload-actions">
              <button 
                type="button" 
                className="upload-btn primary"
                onClick={() => document.getElementById('profilePhoto').click()}
                disabled={isUploading}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7,10 12,15 17,10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                Choose File
              </button>
              <button 
                type="button" 
                className="upload-btn secondary"
                onClick={() => document.getElementById('profilePhoto').click()}
                disabled={isUploading}
                style={{ display: 'none' }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"></path>
                  <circle cx="12" cy="13" r="3"></circle>
                </svg>
                Take Photo
              </button>
            </div>
          </div>

          <div className="photo-upload-section">
            {showSuccessMessage && (
              <div className="success-alert">
                <div className="alert-content">
                  <div className="alert-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                      <polyline points="22,4 12,14.01 9,11.01"></polyline>
                    </svg>
                  </div>
                  <div className="alert-message">
                    <h4>Success!</h4>
                    <p>{successMessage}</p>
                  </div>
                  <button 
                    type="button" 
                    onClick={hideSuccess}
                    className="alert-close"
                    title="Dismiss notification"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>
                <div className="alert-progress">
                  <div className="alert-progress-bar"></div>
                </div>
              </div>
            )}
            
            {errors.general && (
              <div className="error-alert">
                <div className="alert-content">
                  <div className="alert-icon error">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="15" y1="9" x2="9" y2="15"></line>
                      <line x1="9" y1="9" x2="15" y2="15"></line>
                    </svg>
                  </div>
                  <div className="alert-message">
                    <h4>Upload Failed</h4>
                    <p>{errors.general}</p>
                    <div className="error-tips">
                      <span className="tip-icon">💡</span>
                      <span><strong>Tips:</strong> Check your internet connection, try refreshing the page, or contact support if the problem persists.</span>
                    </div>
                  </div>
                  <button 
                    type="button" 
                    onClick={() => setErrors({})}
                    className="alert-close"
                    title="Dismiss error"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>
              </div>
            )}

            <div className="photo-upload-area">
              <input
                ref={fileInputRef}
                type="file"
                id="profilePhoto"
                name="profilePhoto"
                accept="image/*"
                onChange={handleChange}
                className="file-input"
              />
              
              <div 
                className={`upload-zone ${isDragOver ? 'drag-over' : ''}`}
                onClick={handlePhotoUploadClick}
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                style={{ cursor: 'pointer' }}
              >
                <div className="photo-preview">
                {previewPhoto || user.profile_photo_url ? (
                  <div className="photo-preview-container">
                    <img src={previewPhoto || user.profile_photo_url} alt="Profile preview" className="preview-image" />
                    <div className="preview-overlay">
                      <div className="preview-actions">
                        <div className="change-photo-hint">
                          <div className="hint-icon">
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
                              <circle cx="12" cy="13" r="4"></circle>
                            </svg>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}
                {isUploading && (
                  <div className="upload-overlay">
                    <div className="upload-progress-container">
                      <div className="upload-progress-card">
                        <div className="progress-header">
                          <div className="progress-icon">
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                              <polyline points="7,10 12,15 17,10"></polyline>
                              <line x1="12" y1="15" x2="12" y2="3"></line>
                            </svg>
                          </div>
                          <div className="progress-info">
                            <h4>Uploading Photo</h4>
                            <p>Please wait while we process your image...</p>
                          </div>
                        </div>
                        
                        <div className="progress-stats">
                          <div className="progress-percentage">
                            <span className="percentage-number">{uploadProgress}%</span>
                            <span className="percentage-label">Complete</span>
                          </div>
                          <div className="progress-details">
                            <div className="detail-item">
                              <span className="detail-label">Status:</span>
                              <span className="detail-value">Processing</span>
                            </div>
                            <div className="detail-item">
                              <span className="detail-label">Quality:</span>
                              <span className="detail-value">High</span>
                            </div>
                          </div>
                        </div>

                        <div className="upload-progress-section">
                          <div className="progress-header-info">
                            <div className="progress-title">
                              <div className="progress-icon">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                  <polyline points="7,10 12,15 17,10"></polyline>
                                  <line x1="12" y1="15" x2="12" y2="3"></line>
                                </svg>
                              </div>
                              <div className="progress-text">
                                <h4>Uploading Your Photo</h4>
                                <p>
                                  {uploadProgress < 30 ? 'Preparing your image for upload...' : 
                                   uploadProgress < 70 ? 'Uploading to our secure servers...' : 
                                   uploadProgress < 95 ? 'Processing and optimizing...' : 'Finalizing your profile photo...'}
                                </p>
                              </div>
                            </div>
                            <div className="progress-percentage-badge">
                              <span className="percentage-number">{uploadProgress}%</span>
                            </div>
                          </div>
                          
                          <div className="progress-bar-wrapper">
                            <div className="progress-bar-track">
                              <div 
                                className="progress-bar-fill" 
                                style={{ width: `${uploadProgress}%` }}
                              >
                                <div className="progress-bar-shine"></div>
                              </div>
                            </div>
                            <div className="progress-indicators">
                              <div className={`indicator ${uploadProgress > 0 ? 'active' : ''}`}>
                                <div className="indicator-dot"></div>
                                <span>Start</span>
                              </div>
                              <div className={`indicator ${uploadProgress > 25 ? 'active' : ''}`}>
                                <div className="indicator-dot"></div>
                                <span>Upload</span>
                              </div>
                              <div className={`indicator ${uploadProgress > 50 ? 'active' : ''}`}>
                                <div className="indicator-dot"></div>
                                <span>Process</span>
                              </div>
                              <div className={`indicator ${uploadProgress > 75 ? 'active' : ''}`}>
                                <div className="indicator-dot"></div>
                                <span>Optimize</span>
                              </div>
                              <div className={`indicator ${uploadProgress > 95 ? 'active' : ''}`}>
                                <div className="indicator-dot"></div>
                                <span>Complete</span>
                              </div>
                            </div>
                          </div>
                          
                          <div className="progress-stats">
                            <div className="stat-item">
                              <span className="stat-label">File Size</span>
                              <span className="stat-value">2.4 MB</span>
                            </div>
                            <div className="stat-item">
                              <span className="stat-label">Upload Speed</span>
                              <span className="stat-value">1.2 MB/s</span>
                            </div>
                            <div className="stat-item">
                              <span className="stat-label">Time Remaining</span>
                              <span className="stat-value">
                                {uploadProgress < 100 ? `${Math.max(1, Math.ceil((100 - uploadProgress) / 10))}s` : 'Complete'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                </div>
                
                <div className="upload-instructions">
                  <div className="instruction-item">
                    <div className="instruction-icon">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        <polyline points="7,10 12,15 17,10"></polyline>
                        <line x1="12" y1="15" x2="12" y2="3"></line>
                      </svg>
                    </div>
                    <span>Drag and drop your image here</span>
                  </div>
                  <div className="instruction-item">
                    <div className="instruction-icon">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        <polyline points="14,2 14,8 20,8"></polyline>
                        <line x1="16" y1="13" x2="8" y2="13"></line>
                        <line x1="16" y1="17" x2="8" y2="17"></line>
                        <polyline points="10,9 9,9 8,9"></polyline>
                      </svg>
                    </div>
                    <span>Or click to browse files</span>
                  </div>
                </div>
              </div>
              
              {errors.profile_photo && (
                <div className="field-error">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="15" y1="9" x2="9" y2="15"></line>
                    <line x1="9" y1="9" x2="15" y2="15"></line>
                  </svg>
                  <span>{errors.profile_photo}</span>
                </div>
              )}
            </div>
        </div>
      </div>

    </div>
  );
};

  const renderProfileTab = () => {
    if (!user) return <div>Loading...</div>;

    return (
      <div className="profile-tab">



        {/* Profile Details */}
        <div className="profile-details-card">
          <div className="card-header">
            <h3>Profile Information</h3>
          </div>

          {isEditing ? (
            <form onSubmit={handleSubmit} className="profile-form">
              {showSuccessMessage && (
                <div className="success-message">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22,4 12,14.01 9,11.01"></polyline>
                  </svg>
                  <div style={{ flex: 1 }}>
                    <div>{successMessage}</div>
                  </div>
                  <button 
                    type="button" 
                    onClick={hideSuccess}
                    style={{ 
                      background: 'none', 
                      border: 'none', 
                      color: 'inherit', 
                      cursor: 'pointer',
                      padding: '4px',
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    title="Dismiss message"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>
              )}
              
              {errors.general && (
                <div className="error-message general-error">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="15" y1="9" x2="9" y2="15"></line>
                    <line x1="9" y1="9" x2="15" y2="15"></line>
                  </svg>
                  <div style={{ flex: 1 }}>
                    <div>{errors.general}</div>
                    {errors.general.includes('Network') || errors.general.includes('timed out') || errors.general.includes('Server error') ? (
                      <div style={{ marginTop: '8px', fontSize: '14px', opacity: 0.8 }}>
                        💡 <strong>Tips:</strong> Check your internet connection, try refreshing the page, or contact support if the problem persists.
                      </div>
                    ) : null}
                  </div>
                  <button 
                    type="button" 
                    onClick={() => setErrors({})}
                    style={{ 
                      background: 'none', 
                      border: 'none', 
                      color: 'inherit', 
                      cursor: 'pointer',
                      padding: '4px',
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    title="Dismiss error"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>
              )}


              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName">First Name</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    className={errors.firstName || errors.name ? 'error' : ''}
                    placeholder="First name"
                    disabled={isLoading}
                  />
                  {(errors.firstName || errors.name) && <span className="error-text">{errors.firstName || errors.name}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="lastName">Last Name</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    className={errors.lastName || errors.name ? 'error' : ''}
                    placeholder="Last name"
                    disabled={isLoading}
                  />
                  {(errors.lastName || errors.name) && <span className="error-text">{errors.lastName || errors.name}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="phone">Phone Number</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className={errors.phone ? 'error' : ''}
                    placeholder="Enter your phone number"
                    disabled={isLoading}
                  />
                  {errors.phone && <span className="error-text">{errors.phone}</span>}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="dateOfBirth">Date of Birth</label>
                <input
                  type="date"
                  id="dateOfBirth"
                  name="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={handleChange}
                  className={errors.date_of_birth ? 'error' : ''}
                  disabled={isLoading}
                />
                {formData.dateOfBirth && (
                  <p className="age-display">Age: {calculateAge(formData.dateOfBirth)} years</p>
                )}
                {errors.date_of_birth && <span className="error-text">{errors.date_of_birth}</span>}
              </div>

              <div className="form-group">
                <label>Email Address</label>
                <input
                  type="email"
                  value={user.email}
                  disabled
                  className={`email-input ${!user.is_email_verified ? 'unverified-highlight' : 'verified-input'}`}
                />
                <p className="email-status">
                  {user.is_email_verified ? (
                    <span className="verified">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20,6 9,17 4,12"></polyline>
                      </svg>
                      Email verified - You can update your profile
                    </span>
                  ) : (
                    <span className="unverified">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="15" y1="9" x2="9" y2="15"></line>
                        <line x1="9" y1="9" x2="15" y2="15"></line>
                      </svg>
                      Email not verified - Verify your email to update profile
                    </span>
                  )}
                </p>
              </div>

              <div className="form-actions">
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    isUploading ? (
                      <>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                          <polyline points="7,10 12,15 17,10"></polyline>
                          <line x1="12" y1="15" x2="12" y2="3"></line>
                        </svg>
                        Uploading... {uploadProgress}%
                      </>
                    ) : (
                      'Updating...'
                    )
                  ) : (
                    'Save Changes'
                  )}
                </button>
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={() => setIsEditing(false)}
                  disabled={isLoading}
                >
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            <div className="profile-display">
              {/* Basic Information */}
              <div className="info-section">
                <h4 className="section-title">Basic Information</h4>
                <div className="info-item">
                  <label>Full Name</label>
                  <span>{user.name || 'Not provided'}</span>
                </div>
                <div className="info-item">
                  <label>Email Address</label>
                  <span className={!user.is_email_verified ? 'unverified-text' : ''}>{user.email}</span>
                  <span className={`email-status ${user.is_email_verified ? 'verified' : 'unverified'}`}>
                    {user.is_email_verified ? (
                      <>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20,6 9,17 4,12"></polyline>
                        </svg>
                        Email verified
                      </>
                    ) : (
                      <>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10"></circle>
                          <line x1="15" y1="9" x2="9" y2="15"></line>
                          <line x1="9" y1="9" x2="15" y2="15"></line>
                        </svg>
                        Email not verified
                      </>
                    )}
                  </span>
                </div>
                <div className="info-item">
                  <label>Phone Number</label>
                  <span>{user.phone || 'Not provided'}</span>
                </div>
                <div className="info-item">
                  <label>Date of Birth</label>
                  <span key={`dob-${user.date_of_birth}`}>
                    {user.date_of_birth ? new Date(user.date_of_birth).toLocaleDateString() : 'Not provided'}
                  </span>
                  {user.date_of_birth && (
                    <span className="age-info" key={`age-${user.age}`}>({user.age} years old)</span>
                  )}
                </div>
              </div>

              {/* Professional Information */}
              <div className="info-section">
                <h4 className="section-title">Professional Information</h4>
                <div className="info-item">
                  <label>Work Type</label>
                  <span className={`work-type-badge ${user.work_type ? user.work_type.replace('-', '') : 'not-provided'}`}>
                    {user.work_type ? (
                      <>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                          <line x1="8" y1="21" x2="16" y2="21"></line>
                          <line x1="12" y1="17" x2="12" y2="21"></line>
                        </svg>
                        {user.work_type.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </>
                    ) : (
                      'Not specified'
                    )}
                  </span>
                </div>
              </div>

              {/* Location & Preferences */}
              <div className="info-section">
                <h4 className="section-title">Location & Preferences</h4>
                <div className="info-item">
                  <label>Country</label>
                  <span className="country-info">
                    {user.country_code ? (
                      <>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                          <circle cx="12" cy="10" r="3"></circle>
                        </svg>
                        {user.country_code}
                      </>
                    ) : (
                      'Not specified'
                    )}
                  </span>
                </div>
                <div className="info-item">
                  <label>Preferred Work Timezone</label>
                  <span className="timezone-info">
                    {user.timezone ? (
                      <>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10"></circle>
                          <polyline points="12,6 12,12 16,14"></polyline>
                        </svg>
                        {user.timezone_display || user.timezone}
                      </>
                    ) : (
                      'Not specified'
                    )}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* About Section - Hide in Recruiter/Company Mode */}
        {!isRecruiterOrCompanyMode && user.bio && (
          <div className="about-section">
            <div className="about-main">
              <div className="about-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path>
                  <rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect>
                </svg>
              </div>
              <div className="about-info">
                <div className="about-title-section">
                  <h3>About</h3>
                </div>
                <div className="about-content">
                  <p>{user.bio}</p>
                </div>
              </div>
            </div>
          </div>
        )}
        {!isRecruiterOrCompanyMode && !user.bio && (
          <div className="about-section-placeholder" onClick={() => setShowEditModal(true)}>
            <div className="about-main">
              <div className="about-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path>
                  <rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect>
                </svg>
              </div>
              <div className="about-info">
                <div className="about-title-section">
                  <h3>About</h3>
                </div>
                <div className="about-placeholder-content">
                  <div className="placeholder-text">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
                    </svg>
                    <span>Tell us about yourself</span>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="9,18 15,12 9,6"></polyline>
                    </svg>
                  </div>
                  <p className="placeholder-description">Add a brief bio to showcase your skills, experience, and career goals</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Removed renderTabContent function - no longer using tabs

  // Handlers for bottom navigation
  const handleProfileButtonClick = () => {
    setIsProfileSidebarOpen(true);
  };

  const handleProfileStatusClick = () => {
    // Navigate to profile status page
    navigate('/profile/status');
  };

  const handleJobsClick = () => {
    navigate('/vacancies');
  };

  const handleMessagesClick = () => {
    // Navigate to messages page (placeholder)
    // navigate('/messages');
  };

  const handleNotificationsClick = () => {
    // Handled by NotificationSystem component
  };

  if (!user) {
    return <div>Loading...</div>;
  }

  return (
    <div className="App dashboard-page-with-bottom-nav">
      {/* Header - visible on desktop/tablet, hidden on mobile */}
      <div className="dashboard-page-header">
        <Header 
          onLoginClick={() => {}}
          onSignupClick={() => {}}
          isLoggedIn={isAuthenticated}
          user={user}
          onHomeClick={onHomeClick}
          onCategoriesClick={onCategoriesClick}
          onHowItWorksClick={onHowItWorksClick}
          onProfileClick={onProfileClick}
          onAccountSettingsClick={onAccountSettingsClick}
          onForumClick={onForumClick}
          onJobsClick={handleJobsClick}
          currentPage="profile"
        />
      </div>
      <div className="dashboard-page user-dashboard">
        {/* Hidden file input for avatar upload */}
        <input
          ref={fileInputRef}
          type="file"
          id="profilePhoto"
          name="profilePhoto"
          accept="image/*"
          onChange={handleChange}
          style={{ display: 'none' }}
        />
        {/* Hidden file input for banner upload */}
        <input
          ref={bannerInputRef}
          type="file"
          id="bannerImage"
          name="bannerImage"
          accept="image/*"
          onChange={handleBannerChange}
          style={{ display: 'none' }}
        />
        {/* Hidden file input for resume upload */}
        <input
          ref={resumeInputRef}
          type="file"
          id="resume"
          name="resume"
          accept=".pdf,.doc,.docx"
          onChange={handleResumeChange}
          style={{ display: 'none' }}
        />
        <div className="dashboard-container">
          {/* Profile Header Section */}
          <div className="profile-header-section">
            {/* Profile Overview Section with LinkedIn-style Layout */}
            <div className="header-section linkedin-style-profile">
              {/* Banner/Cover Image Section */}
              <div 
                className="cover-image-section"
                onClick={(e) => {
                  // Only handle clicks on the section itself (not on buttons or image)
                  if (e.target === e.currentTarget || e.target.classList.contains('cover-image-section')) {
                    if (!bannerPreview && !user.banner_image_url) {
                      // If no banner, open file picker
                      handleBannerUpload(e);
                    }
                  }
                }}
                title={bannerPreview || user.banner_image_url ? "Click to view banner image" : "Click to upload banner image"}
                style={{ cursor: bannerPreview || user.banner_image_url ? 'pointer' : 'pointer' }}
              >
                {bannerPreview || (user && user.banner_image_url) ? (
                  <img 
                    src={bannerPreview || user.banner_image_url} 
                    alt="Profile Banner" 
                    className="cover-image"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setImageViewType('banner');
                      // Small delay to ensure state is set before opening modal
                      setTimeout(() => {
                        setShowImageViewModal(true);
                      }, 0);
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      const placeholder = e.target.nextElementSibling;
                      if (placeholder) {
                        placeholder.style.display = 'flex';
                      }
                    }}
                  />
                ) : null}
                {!bannerPreview && !(user && user.banner_image_url) && (
                  <div className="cover-image-placeholder">
                    <div className="placeholder-content">
                      <div className="placeholder-text">
                        <h4>Your Professional Brand Awaits</h4>
                        <p>First impressions matter! Upload a stunning banner that reflects your expertise and attracts opportunities</p>
                        <div className="upload-hint">
                          <button 
                            type="button"
                            className="upload-hint-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleBannerUpload(e);
                            }}
                          >
                            Upload now & make an impact
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Cover Image Edit Buttons */}
                <div className="cover-edit-overlay">
                  <div className="cover-edit-buttons">
                    <button 
                      className="cover-edit-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setImageViewType('banner');
                        handleBannerUpload(e);
                      }}
                      title="Change Banner Image"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
                      </svg>
                    </button>
                    {(bannerPreview || user.banner_image_url) && (
                      <button 
                        className="cover-remove-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveBanner();
                        }}
                        title="Remove Banner Image"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3,6 5,6 21,6"></polyline>
                          <path d="M19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"></path>
                          <line x1="10" y1="11" x2="10" y2="17"></line>
                          <line x1="14" y1="11" x2="14" y2="17"></line>
                        </svg>
                      </button>
                    )}
                  </div>
                </div>

                {/* Upload Progress */}
                {isBannerUploading && (
                  <div className="banner-upload-progress">
                    <div className="progress-bar">
                      <div 
                        className="progress-fill" 
                        style={{ width: `${bannerUploadProgress}%` }}
                      ></div>
                    </div>
                    <span className="progress-text">{Math.round(bannerUploadProgress)}%</span>
                  </div>
                )}
              </div>

              {/* Profile Information Section */}
              <div className="profile-info-section">
              {/* Dynamic Achievement Badge and Public View Button - Top Right Corner */}
              <div className="achievement-badge-corner">
                <div className="badge-and-button-container">
                  {getAchievementBadge()}
                  <button 
                    className="public-view-btn"
                    onClick={handlePublicView}
                    title={user?.username ? "View Public Profile" : "Set username to view your public profile"}
                    data-has-username={!!user?.username}
                  >
                    {user?.username ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                        <circle cx="12" cy="12" r="3"></circle>
                      </svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                        <circle cx="12" cy="7" r="4"></circle>
                        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                      </svg>
                    )}
                    <span>{user?.username ? 'View as Public' : 'Set Username'}</span>
                  </button>
                </div>
              </div>

                <div className="profile-info-layout">
                  {/* Profile Picture - Overlapping */}
                  <div 
                    className="profile-picture-overlapping"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAvatarPhotoUpload(e);
                    }}
                    title={previewPhoto || user.profile_photo_url ? "Click to view profile picture" : "Click to upload profile picture"}
                    style={{ cursor: 'pointer' }}
                  >
                    {previewPhoto || user.profile_photo_url ? (
                      <img 
                        src={previewPhoto || user.profile_photo_url} 
                        alt="Profile"
                        onClick={(e) => {
                          e.stopPropagation();
                          setImageViewType('profile');
                          // Small delay to ensure state is set before opening modal
                          setTimeout(() => {
                            setShowImageViewModal(true);
                          }, 0);
                        }}
                        style={{ cursor: 'pointer' }}
                      />
                    ) : (
                      <div className="avatar-placeholder">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                          <circle cx="12" cy="7" r="4"></circle>
                        </svg>
                      </div>
                    )}
                    <div className="avatar-overlay">
                      <button 
                        className="avatar-edit-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setImageViewType('profile');
                          handleAvatarPhotoUpload(e);
                        }}
                        title="Change Profile Picture"
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* User Details */}
                  <div className={`user-details ${user.professional_title ? 'has-professional-title' : ''}`}>
                    <div className="user-name-section">
                      <div className={`name-title-container ${!user.professional_title ? 'no-professional-title' : ''}`} ref={nameTitleContainerRef}>
                        {/* Name and Edit Button Row */}
                        <div className="name-edit-row">
                          <h2>
                            {user.name || 'User'}
                          </h2>
                          {/* Edit button for desktop view - next to name */}
                          <button 
                            className="edit-profile-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowEditModal(true);
                            }}
                            title="Edit Profile"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
                            </svg>
                          </button>
                        </div>
                        {/* Professional title below the name */}
                        <div 
                          className={`professional-title-block ${!user.professional_title ? 'professional-title-placeholder' : ''}`} 
                          ref={professionalTitleRef}
                          onClick={!user.professional_title ? (e) => {
                            e.stopPropagation();
                            setShowEditModal(true);
                          } : undefined}
                          style={!user.professional_title ? { cursor: 'pointer' } : {}}
                        >
                          {user.professional_title || (
                            <span className="professional-title-placeholder-text">Add professional title</span>
                          )}
                          {/* Inline edit button for mobile/tablet only - always visible on mobile */}
                          <button 
                            ref={editButtonRef}
                            className="edit-profile-btn inline-edit-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowEditModal(true);
                            }}
                            title="Edit Profile"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>

                    {user.country && (
                      <div className="user-location-section">
                        <span className="user-location">{user.country}</span>
                      </div>
                    )}
                    
                    {(user.username || user.work_type || user.job_seeking_status) && (
                      <div className="profile-details-section">
                        

                        {user.job_seeking_status && (
                          <span className={`job-seeking-status ${user.job_seeking_status === 'actively-seeking' ? 'actively-seeking' : ''}`}>
                            {user.job_seeking_status === 'actively-seeking' ? (
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-status={user.job_seeking_status}>
                                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"></path>
                                <path d="M8 12l2 2 4-4"></path>
                              </svg>
                            ) : (
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-status={user.job_seeking_status}>
                                <path d="M9 12l2 2 4-4"></path>
                                <path d="M21 12c.552 0 1-.448 1-1V5c0-.552-.448-1-1-1H3c-.552 0-1 .448-1 1v6c0 .552.448 1 1 1h18z"></path>
                                <path d="M3 12v6c0 .552.448 1 1 1h16c.552 0 1-.448 1-1v-6"></path>
                              </svg>
                            )}
                            {user.job_seeking_status === 'actively-seeking' ? (
                              <span className="status-text">
                                Actively Seeking
                              </span>
                            ) : (
                              <span className="status-text">
                                {user.job_seeking_status.charAt(0).toUpperCase() + user.job_seeking_status.slice(1).replace(/-/g, ' ')}
                              </span>
                            )}
                          </span>
                        )}
                        
                        {user.work_type && (
                          <span className="work-preference">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                              <line x1="8" y1="21" x2="16" y2="21"></line>
                              <line x1="12" y1="17" x2="12" y2="21"></line>
                            </svg>
                            {user.work_type.charAt(0).toUpperCase() + user.work_type.slice(1).replace('-', ' ')}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bio Section - Hide in Recruiter/Company Mode */}
          {!isRecruiterOrCompanyMode && (
            <div className="header-section">
              <div className="section-header">
                <h3>About Me</h3>
                {/* Hide button in section-header on mobile, show it inside bio-container instead */}
                {user.bio ? (
                  <button 
                    className="edit-bio-btn header-btn desktop-bio-btn"
                    onClick={() => setShowEditModal(true)}
                    title="Edit Bio"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
                    </svg>
                    Edit Bio
                  </button>
                ) : (
                  <button 
                    className="add-bio-btn header-btn desktop-bio-btn"
                    onClick={() => setShowEditModal(true)}
                    title="Add Bio"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="12" y1="5" x2="12" y2="19"></line>
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                    </svg>
                    Add Bio
                  </button>
                )}
              </div>
              <div className="bio-container">
                {user.bio ? (
                  <>
                    <div className="bio-content">
                      {/* Mobile heading and edit button row */}
                      <div className="bio-header-row">
                        <h3 className="bio-mobile-heading">About</h3>
                        <button 
                          className="edit-bio-btn header-btn mobile-bio-btn"
                          onClick={() => setShowEditModal(true)}
                          title="Edit Bio"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
                          </svg>
                          Edit Bio
                        </button>
                      </div>
                      <p>{user.bio}</p>
                    </div>
                  </>
                ) : (
                  <div className="no-bio">
                    {/* Mobile add button - shown above "Tell Your Story" */}
                    <button 
                      className="add-bio-btn header-btn mobile-bio-btn"
                      onClick={() => setShowEditModal(true)}
                      title="Add Bio"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="12" y1="5" x2="12" y2="19"></line>
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                      </svg>
                      Add Bio
                    </button>
                    <div className="no-bio-icon">
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path>
                        <rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect>
                      </svg>
                    </div>
                    <div className="no-bio-content">
                      <h4>Tell Your Story</h4>
                      <p>Add a brief bio to showcase your personality and professional background</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Dashboard Content Wrapper */}
          <div className="dashboard-content">
            {/* Left Sidebar for Skills and Basic Information */}
            <div className="profile-left-sidebar">
              <div className="sidebar-section">
                <h3>Contact Information</h3>
                <div className="contact-item">
                  <div className="contact-content">
                    <span className="contact-label">Email Address</span>
                    <span className="contact-value">{user.email}</span>
                  </div>
                </div>
                <div className="contact-item">
                  <div className="contact-content">
                    <span className="contact-label">Phone Number</span>
                    <span className="contact-value">{user.phone || 'Add your phone'}</span>
                  </div>
                </div>
                <div className="contact-item">
                  <div className="contact-content">
                    <span className="contact-label">Timezone</span>
                    <div className="contact-value-container">
                      <span 
                        className="contact-value"
                        title={(() => {
                          try {
                            const authUser = JSON.parse(localStorage.getItem('auth_user'));
                            if (authUser && (authUser.timezone_display || authUser.timezone)) {
                              return authUser.timezone_display || authUser.timezone;
                            }
                            return 'Not set';
                          } catch (error) {
                            return 'Not set';
                          }
                        })()}
                      >
                        {(() => {
                          try {
                            const authUser = JSON.parse(localStorage.getItem('auth_user'));
                            if (authUser && (authUser.timezone_display || authUser.timezone)) {
                              const timezoneText = authUser.timezone_display || authUser.timezone;
                              const words = timezoneText.split(' ');
                              if (words.length > 10) {
                                return words.slice(0, 10).join(' ') + '...';
                              }
                              return timezoneText;
                            }
                            return 'Not set';
                          } catch (error) {
                            return 'Not set';
                          }
                        })()}
                      </span>
                      {(() => {
                        try {
                          const authUser = JSON.parse(localStorage.getItem('auth_user'));
                          if (!authUser || (!authUser.timezone_display && !authUser.timezone)) {
                            return (
                              <button 
                                className="edit-timezone-btn"
                                onClick={() => setShowEditModal(true)}
                                title="Set your timezone"
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <line x1="12" y1="5" x2="12" y2="19"></line>
                                  <line x1="5" y1="12" x2="19" y2="12"></line>
                                </svg>
                              </button>
                            );
                          }
                          return (
                            <button 
                              className="edit-timezone-btn"
                              onClick={() => setShowEditModal(true)}
                              title="Edit timezone"
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="12" y1="5" x2="12" y2="19"></line>
                                <line x1="5" y1="12" x2="19" y2="12"></line>
                              </svg>
                            </button>
                          );
                        } catch (error) {
                          return (
                            <button 
                              className="edit-timezone-btn"
                              onClick={() => setShowEditModal(true)}
                              title="Set your timezone"
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="12" y1="5" x2="12" y2="19"></line>
                                <line x1="5" y1="12" x2="19" y2="12"></line>
                              </svg>
                            </button>
                          );
                        }
                      })()}
                    </div>
                  </div>
                </div>
              </div>

              {/* Username Management Section */}
              <div className="sidebar-section">
                <h3>Public Profile</h3>
                <div className="contact-item">
                  <div className="contact-content">
                    <span className="contact-label">Username</span>
                    <div className="contact-value-container">
                      <span className="contact-value">
                        {user?.username ? `@${user.username}` : 'Not set'}
                      </span>
                      {(user?.can_change_username !== false) && (
                        <button 
                          className="edit-username-btn"
                          onClick={() => setShowUsernameModal(true)}
                          title={user.username ? "Edit username" : "Create username"}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            {user.username ? (
                              <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
                            ) : (
                              <path d="M12 5v14M5 12h14"></path>
                            )}
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                {user.username_changes_count !== undefined && (
                  <div className="username-info">
                    <span className="username-changes">
                      {user.username 
                        ? `Changes remaining: ${user.remaining_username_changes || 0}/3`
                        : 'Create your unique username for your public profile'
                      }
                    </span>
                  </div>
                )}
              </div>

              {/* Skills Section - Hide in Recruiter/Company Mode */}
              {!isRecruiterOrCompanyMode && (
                <div className="sidebar-section">
                  <div className="section-header">
                    <h3>Skills</h3>
                    <button 
                      className="header-btn add-skills-btn"
                      onClick={() => setShowSkillsPopup(true)}
                      title="Add Skills"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="12" y1="5" x2="12" y2="19"></line>
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                      </svg>
                      <span>Add</span>
                    </button>
                  </div>
                  <div className="skills-container">
                    {skillsLoading ? (
                      <div className="skills-loading">
                        <div className="spinner-small"></div>
                        <span>Loading skills...</span>
                      </div>
                    ) : userSkills && userSkills.length > 0 ? (
                      <div className="skills-list">
                        {userSkills.map((userSkill) => (
                          <span key={userSkill.id} className="skill-tag">
                            {userSkill.skill?.name || userSkill.name || 'Unknown Skill'}
                            <button 
                              className="skill-remove-btn"
                              onClick={() => handleRemoveSkill(userSkill.id)}
                              title="Remove skill"
                            >
                              <i className="bi bi-x"></i>
                            </button>
                          </span>
                        ))}
                      </div>
                    ) : (
                      <div className="no-skills">
                        <button 
                          className="no-skills-icon add-skills-icon"
                          onClick={() => setShowSkillsPopup(true)}
                          title="Add Skills"
                        >
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="12" y1="5" x2="12" y2="19"></line>
                            <line x1="5" y1="12" x2="19" y2="12"></line>
                          </svg>
                        </button>
                        <div className="no-skills-content">
                          <h4>Showcase Your Skills</h4>
                          <p>Add your professional skills to help others understand your expertise</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Resume Section - Hide in Recruiter/Company Mode */}
              {!isRecruiterOrCompanyMode && (
                <div className="sidebar-section">
                  <div className="section-header">
                    <h3>Resume</h3>
                    <button 
                      className="header-btn manage-resume-btn"
                      onClick={handleResumeUpload}
                      title="Manage Resumes"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 3v18m-9-9h18"></path>
                      </svg>
                      <span>Manage</span>
                    </button>
                  </div>
                  <div className="resume-container">
                    {resumeLoading ? (
                      <div className="resume-loading">
                        <div className="spinner-small"></div>
                        <span>Loading resume...</span>
                      </div>
                    ) : userResume ? (
                      <div className="resume-item">
                        <div className="resume-info">
                          <div className="resume-icon">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                              <polyline points="14,2 14,8 20,8"></polyline>
                              <line x1="16" y1="13" x2="8" y2="13"></line>
                              <line x1="16" y1="17" x2="8" y2="17"></line>
                              <polyline points="10,9 9,9 8,9"></polyline>
                            </svg>
                          </div>
                          <div className="resume-details">
                            <span className="resume-name">{userResume.name || 'Resume'}</span>
                            <span className="resume-size">{userResume.size ? `${(userResume.size / 1024).toFixed(1)} KB` : ''}</span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="no-resume">
                        <div className="no-resume-icon">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                            <polyline points="14,2 14,8 20,8"></polyline>
                          </svg>
                        </div>
                        <div className="no-resume-content">
                          <h4>No Resume Selected</h4>
                          <p>Click "Manage" to upload or select a resume</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

          {/* Center Content for Show Your Talent */}
          <div className="profile-center-content">
            {/* Show Job Posting CTA when in Recruiter/Company Mode */}
            {isRecruiterOrCompanyMode && (
              <JobPostingCTA />
            )}
            
            {/* Personal Projects Section - Hide in Recruiter/Company Mode */}
            {!isRecruiterOrCompanyMode && (
              <PersonalProjects 
              userProjects={userProjects}
              setUserProjects={setUserProjects}
              projectsLoading={projectsLoading}
              setProjectsLoading={setProjectsLoading}
              getCachedProjects={getCachedProjects}
              onEditProject={handleEditProject}
              onRemoveProject={handleRemoveProject}
              onShowAllProjects={() => setShowAllProjectsPopup(true)}
              onAddProject={() => {
                setEditingProject(null);
                setShowProjectPopup(true);
              }}
            />
            )}
            
            {/* Personal Education Section - Hide in Recruiter/Company Mode */}
            {!isRecruiterOrCompanyMode && (
              <PersonalEducation 
              userEducation={userEducation}
              setUserEducation={setUserEducation}
              educationLoading={educationLoading}
              setEducationLoading={setEducationLoading}
              getCachedEducation={getCachedEducation}
              onEditEducation={handleEditEducation}
              onRemoveEducation={handleRemoveEducation}
              onShowAllEducation={() => setShowAllEducationPopup(true)}
              onAddEducation={() => {
                setEditingEducation(null);
                setShowEducationPopup(true);
              }}
            />
            )}
            
            {/* Personal Experience Section - Hide in Recruiter/Company Mode */}
            {!isRecruiterOrCompanyMode && (
              <PersonalExperience 
              userExperiences={userExperiences}
              setUserExperiences={setUserExperiences}
              experiencesLoading={experiencesLoading}
              setExperiencesLoading={setExperiencesLoading}
              getCachedExperiences={getCachedExperiences}
              onEditExperience={handleEditExperience}
              onRemoveExperience={handleRemoveExperience}
              onShowAllExperiences={() => setShowAllExperiencesPopup(true)}
              onAddExperience={() => {
                        setEditingExperience(null);
                        setShowExperiencePopup(true);
              }}
            />
            )}
          </div>

          {/* Right Sidebar - Profile Status */}
          <div className="profile-right-sidebar">
              {/* Show Profile Setup Timeline in Personal Mode */}
              {!isRecruiterOrCompanyMode && (
                <ProfileSetupTimeline
                  user={user}
                  completionStatus={completionStatus}
                  profileCompletion={profileCompletion}
                  userSkills={userSkills}
                  userProjects={userProjects}
                  setShowEditModal={setShowEditModal}
                  setShowSkillsPopup={setShowSkillsPopup}
                  setShowProjectPopup={setShowProjectPopup}
                  onAccountSettingsClick={onAccountSettingsClick}
                />
              )}
              
            {/* Show Job Stats in Recruiter/Company Mode */}
            {isRecruiterOrCompanyMode && (
              <JobStats user={user} />
            )}
          </div>
          </div>
        </div>
      </div>
      <Footer />
      
      {/* Profile Edit Modal */}
      <ProfileEditModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        user={user}
        updateUser={updateUser}
        onSuccess={handleModalSuccess}
      />

      {/* Skills Popup */}
      <SkillsPopup
        isOpen={showSkillsPopup}
        onClose={() => setShowSkillsPopup(false)}
        onSave={handleSkillsSave}
        userSkills={userSkills}
      />

        {/* Experience Popup */}
        <ExperiencePopup
          isOpen={showExperiencePopup}
          onClose={() => {
            setShowExperiencePopup(false);
            setEditingExperience(null);
          }}
          onSave={handleExperienceSave}
          experience={editingExperience}
          isEdit={!!editingExperience}
        />

        {/* All Experiences Popup */}
        <AllExperiencesPopup
          isOpen={showAllExperiencesPopup}
          onClose={() => setShowAllExperiencesPopup(false)}
          userExperiences={userExperiences}
          onEditExperience={handleEditExperience}
          onDeleteExperience={handleRemoveExperience}
        />

        {/* Multi-Step Project Popup */}
        <SimpleProjectPopup
          isOpen={showProjectPopup}
          onClose={() => {
            setShowProjectPopup(false);
            setEditingProject(null);
          }}
          onSuccess={handleProjectSave}
          project={editingProject}
          isEdit={!!editingProject}
        />

        {/* All Projects Popup */}
        <AllProjectsPopup
          isOpen={showAllProjectsPopup}
          onClose={() => setShowAllProjectsPopup(false)}
          userProjects={userProjects}
          onEditProject={handleEditProject}
          onDeleteProject={handleRemoveProject}
        />

        {/* Education Popup */}
        <EducationPopup
          isOpen={showEducationPopup}
          onClose={() => {
            setShowEducationPopup(false);
            setEditingEducation(null);
          }}
          onSave={handleEducationSave}
          editingEducation={editingEducation}
        />

        {/* All Education Popup */}
        <AllEducationPopup
          isOpen={showAllEducationPopup}
          onClose={() => setShowAllEducationPopup(false)}
          userEducation={userEducation}
          onEditEducation={handleEditEducation}
          onRemoveEducation={handleRemoveEducation}
          onAddEducation={() => {
            setEditingEducation(null);
            setShowEducationPopup(true);
          }}
        />

      {/* Username Modal */}
      {showUsernameModal && (
        <UsernameModal
          isOpen={showUsernameModal}
          onClose={() => setShowUsernameModal(false)}
          user={user}
          updateUser={updateUser}
          onSuccess={handleModalSuccess}
        />
      )}

      {/* Image View Modal */}
      {showImageViewModal && (
        <ImageViewModal
          isOpen={showImageViewModal}
          onClose={() => {
            setShowImageViewModal(false);
            // Reset to default after closing
            setImageViewType('profile');
            // Clear stored files
            setBannerImage(null);
            setFormData(prev => ({ ...prev, profilePhoto: null }));
            if (fileInputRef.current) {
              fileInputRef.current.value = '';
            }
            if (bannerInputRef.current) {
              bannerInputRef.current.value = '';
            }
          }}
          imageUrl={
            imageViewType === 'profile' 
              ? (previewPhoto || user?.profile_photo_url || null) 
              : (bannerPreview || user?.banner_image_url || null)
          }
          imageType={imageViewType}
          onChangeImage={handleImageChangeFromModal}
          onRemoveImage={handleImageRemoveFromModal}
          isUploading={imageViewType === 'profile' ? isUploading : isBannerUploading}
          uploadProgress={imageViewType === 'profile' ? uploadProgress : bannerUploadProgress}
          initialFile={
            imageViewType === 'banner' 
              ? bannerImage 
              : (imageViewType === 'profile' && formData.profilePhoto ? formData.profilePhoto : null)
          }
        />
      )}

      {/* Resume Management Popup */}
      <ResumeManagementPopup
        isOpen={showResumePopup}
        onClose={() => {
          setShowResumePopup(false);
          // Refresh the resume when popup closes to ensure we have the latest default resume
          fetchUserResume();
        }}
        onResumeSelect={handleResumeSelect}
      />

      {/* Bottom Navigation Bar */}
      <BottomNavigationBar
        onHomeClick={onHomeClick}
        onProfileClick={handleProfileButtonClick}
        onAccountSettingsClick={onAccountSettingsClick}
        onJobsClick={handleJobsClick}
        onMessagesClick={handleMessagesClick}
        onNotificationsClick={handleNotificationsClick}
        onForumClick={onForumClick}
      />

      {/* Profile Sidebar */}
      <ProfileSidebar
        isOpen={isProfileSidebarOpen}
        onClose={() => setIsProfileSidebarOpen(false)}
        onProfileClick={onProfileClick}
        onAccountSettingsClick={onAccountSettingsClick}
        onProfileStatusClick={handleProfileStatusClick}
      />

    </div>
  );
};

export default UserDashboard;
